import { useParams, Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { motion } from "framer-motion";

const LAST_UPDATED = "December 2025";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    intro: "This Privacy Policy describes how Rxn3D LLC (\"Rxn3D\", \"we\", \"us\") collects, uses and shares information when you use our marketing website and product.",
    sections: [
      { h: "Information we collect", p: "We collect information you provide directly (name, email, company, message) when you fill out our contact or demo forms. We also collect information automatically (device, browser, IP, usage) through cookies and standard analytics." },
      { h: "How we use information", p: "We use your information to respond to inquiries, provide the service you request, maintain security, comply with legal obligations, and improve our product." },
      { h: "Sharing", p: "We share information with service providers under strict agreements, and when required by law. We do not sell personal information." },
      { h: "Retention", p: "We retain personal data for as long as necessary for the purposes described above and per applicable law." },
      { h: "Your rights", p: "Depending on your region, you may have rights to access, correct or delete your personal data. Contact us via the contact form for privacy requests." },
      { h: "Contact", p: "For privacy questions, use the contact form on our website." },
    ],
  },
  terms: {
    title: "Terms of Service",
    intro: "These Terms govern your use of the Rxn3D marketing website and product.",
    sections: [
      { h: "Acceptance of terms", p: "By accessing Rxn3D, you agree to these Terms and our Privacy Policy." },
      { h: "Accounts & access", p: "You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account." },
      { h: "Acceptable use", p: "You agree not to misuse the service, violate laws, or infringe on the rights of others. See our Acceptable Use Policy." },
      { h: "Subscriptions", p: "Paid plans are governed by our Subscription Billing Policy. Prices, features and terms may change with notice." },
      { h: "Intellectual property", p: "The service and content are owned by Rxn3D or its licensors and protected by applicable laws." },
      { h: "Disclaimer & liability", p: "The service is provided \"as is\", to the fullest extent permitted by law. Our aggregate liability is limited as described in these Terms." },
      { h: "Termination", p: "We may suspend or terminate access for violations of these Terms or as required by law." },
      { h: "Governing law", p: "These Terms are governed by the laws applicable to Rxn3D LLC." },
    ],
  },
  refund: {
    title: "Refund Policy",
    intro: "Rxn3D handles refunds fairly and on a case-by-case basis.",
    sections: [
      { h: "Monthly plans", p: "Monthly subscriptions are non-refundable once the billing period has started, but you can cancel to avoid future charges." },
      { h: "How to request", p: "Use the contact form with your workspace details and reason. Refunds are processed to the original payment method." },
    ],
  },
  cancellation: {
    title: "Cancellation Policy",
    intro: "You may cancel your Rxn3D subscription at any time.",
    sections: [
      { h: "Monthly plans", p: "Cancellations take effect at the end of the current billing cycle. You retain access until then." },
      { h: "Data export", p: "You may export your data at any time before or after cancellation. Data is retained for a limited period per policy before deletion." },
    ],
  },
  billing: {
    title: "Subscription Billing Policy",
    intro: "Rxn3D is billed as a subscription (SaaS). This policy explains how billing works.",
    sections: [
      { h: "Billing cycles", p: "We currently offer monthly subscriptions only. Billing starts after workspace activation." },
      { h: "Payment methods", p: "Card and bank transfer. Enterprise plans support invoicing." },
      { h: "Auto-renewal", p: "Subscriptions renew automatically at the end of each billing cycle unless cancelled." },
      { h: "Failed payments", p: "We retry failed charges and notify you. Continued failure may pause access." },
      { h: "Taxes", p: "Applicable taxes apply based on your billing region and are billed additionally where required." },
    ],
  },
  "acceptable-use": {
    title: "Acceptable Use Policy",
    intro: "Rxn3D is a shared service and requires responsible use.",
    sections: [
      { h: "Prohibited use", p: "No illegal activity, no infringement of others' rights, no attempts to compromise security or availability of the service." },
      { h: "Content responsibility", p: "You are responsible for the content you and your team upload, including compliance with applicable regulations." },
      { h: "Enforcement", p: "Violations may result in suspension or termination and, where appropriate, reporting to authorities." },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    intro: "We use cookies and similar technologies to run our website and improve your experience.",
    sections: [
      { h: "Essential", p: "Required for the website to function (session, security, preferences)." },
      { h: "Analytics", p: "Used to understand aggregate usage patterns and improve the product." },
      { h: "Marketing", p: "Used, only with consent where required, to measure and improve our outreach." },
      { h: "Controls", p: "You can control cookies in your browser settings and via any cookie banner we present." },
    ],
  },
  security: {
    title: "Security Policy",
    intro: "This policy describes the security controls Rxn3D applies to protect data and service integrity.",
    sections: [
      { h: "Encryption", p: "TLS 1.2+ in transit and AES-256 at rest for stored data." },
      { h: "Access", p: "Granular role-based access controls with least-privilege defaults. SSO on Enterprise." },
      { h: "Backups", p: "Automated daily snapshots and point-in-time recovery for critical records." },
      { h: "Monitoring", p: "24/7 monitoring, audit logs and alerting for anomalous behavior." },
      { h: "Reporting", p: "For security disclosures, use the contact form and mark your message as a security disclosure." },
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const doc = CONTENT[slug] || CONTENT.privacy;

  return (
    <>
      <PageHero
        chapter="§ Legal"
        eyebrow={doc.title}
        title={doc.title}
        description={doc.intro}
      >
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(CONTENT).map(([k, v]) => (
            <Link
              key={k}
              to={`/legal/${k}`}
              data-testid={`legal-tab-${k}`}
              className={`px-3 py-1.5 rounded-full transition-colors ${
                k === slug ? "bg-foreground text-background" : "hairline text-muted-foreground hover:text-foreground"
              }`}
            >
              {v.title}
            </Link>
          ))}
        </div>
      </PageHero>

      <section data-testid="legal-body" className="pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-xs text-muted-foreground mb-8">Last updated · {LAST_UPDATED}</div>
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            {doc.sections.map((s, i) => (
              <motion.div
                key={s.h}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="py-6 border-b hairline last:border-b-0"
                data-testid={`legal-section-${i}`}
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
                  <div>
                    <h3 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight">{s.h}</h3>
                    <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">{s.p}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
